/**
 * Step-by-step export instructions for popular EHRs.
 *
 * Shared by the Import Patients page and the Intake Hub EHR migration panel.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ExportStep {
  text: string;
  /** Placeholder path for a future screenshot or GIF. */
  mediaPlaceholder?: string;
}

export interface ExportSection {
  title: string;
  steps: ExportStep[];
  warning?: string;
}

export interface EhrGuide {
  name: string;
  subtitle?: string;
  overview: string;
  sections: ExportSection[];
  officialGuideUrl?: string;
  /** Tags shown as chips: e.g. "CSV", "XML", "Contact Support" */
  tags: string[];
}

// ─── Guide Data ─────────────────────────────────────────────────────────────

export const EHR_GUIDES: EhrGuide[] = [
  {
    name: 'SimplePractice',
    overview:
      'Use the Client Detail Report to export demographics and insurance, and the Appointments Report to export appointment history.',
    tags: ['CSV'],
    officialGuideUrl:
      'https://support.simplepractice.com/hc/en-us/articles/13218379819661-Understanding-your-clients-and-appointments-reports#clientdeta',
    sections: [
      {
        title: 'Export Client Details (Demographics & Insurance)',
        steps: [
          {
            text: 'Go to Analytics > Reports > Client Detail Report.',
            mediaPlaceholder: '[Screenshot: SimplePractice Analytics > Reports menu]',
          },
          { text: 'Use the filters to select All Clients (or a specific clinician).' },
          { text: 'The report includes: client name, DOB, contact info, and insurance details.' },
          {
            text: 'Click Export to download as a CSV file.',
            mediaPlaceholder:
              '[Screenshot: SimplePractice Client Detail Report with Export button]',
          },
        ],
      },
      {
        title: 'Export Appointments',
        steps: [
          {
            text: 'Go to Analytics > Reports > Appointments Report.',
            mediaPlaceholder: '[Screenshot: SimplePractice Appointments Report]',
          },
          { text: 'Set the date range to cover all the appointments you want to export.' },
          { text: 'Click Export to download as a CSV file.' },
        ],
      },
    ],
  },
  {
    name: 'TherapyNotes',
    overview: 'Export your patient list as a spreadsheet from the Patients tab.',
    tags: ['Excel'],
    officialGuideUrl:
      'https://support.therapynotes.com/hc/en-us/articles/30661278579227-Download-and-Print-Notes',
    sections: [
      {
        title: 'Export Patient List (Demographics)',
        steps: [
          { text: 'Go to the Patients tab.' },
          {
            text: 'Click Export Spreadsheet in the top right.',
            mediaPlaceholder:
              '[Screenshot: TherapyNotes Patients tab with Export Spreadsheet button highlighted]',
          },
          { text: 'Export both Active and Inactive patient lists separately.' },
        ],
      },
    ],
  },
  // {
  //   name: 'TheraNest / Ensora Health',
  //   subtitle: 'formerly TheraNest',
  //   overview:
  //     'Export your client list from Organization settings. Appointment data is available via reports. Bulk session note export requires contacting support.',
  //   tags: ['CSV', 'Contact Support for notes'],
  //   officialGuideUrl:
  //     'https://support.ensorahealth.com/s/article/360052323872-Export-Client-List-or-Data-TheraNest',
  //   sections: [
  //     {
  //       title: 'Export Client List',
  //       steps: [
  //         { text: 'Log in as Organization Administrator.' },
  //         {
  //           text: 'Go to Organization > Export Data.',
  //           mediaPlaceholder: '[Screenshot: TheraNest/Ensora Organization > Export Data menu]',
  //         },
  //         { text: 'Download the client list (includes active and archived clients).' },
  //       ],
  //     },
  //     {
  //       title: 'Export Appointments',
  //       steps: [
  //         { text: 'Go to the Appointment report.' },
  //         { text: 'Set the date range from before your start date to beyond today.' },
  //         { text: 'Export to capture all past and upcoming appointments.' },
  //       ],
  //     },
  //     {
  //       title: 'Export Clinical Notes',
  //       steps: [
  //         { text: 'Contact Ensora Health support to request a bulk export of session notes.' },
  //       ],
  //       warning: 'Self-serve bulk export of session notes is not available.',
  //     },
  //   ],
  // },
  {
    name: 'Kareo / Tebra',
    overview:
      'Export patient demographics and insurance data from Practice Settings. Data exports as XML Summary of Care files.',
    tags: ['XML', 'CSV'],
    officialGuideUrl:
      'https://helpme.tebra.com/Platform/Practice_Settings/Data_Management/Export_Patient_Clinical_Data',
    sections: [
      {
        title: 'Export Patient Data',
        steps: [
          {
            text: 'Go to Practice Settings > Data Management > Export Patient Data.',
            mediaPlaceholder: '[Screenshot: Tebra Practice Settings > Data Management menu]',
          },
          { text: 'Select provider and date range.' },
          { text: 'Click Run Export — downloads as individual XML files per patient.' },
        ],
      },
    ],
  },
  {
    name: 'DrChrono',
    overview:
      'Use the EHI Export (self-serve) to download patient demographics, insurance, and appointment data as CSV files.',
    tags: ['CSV', 'ZIP'],
    officialGuideUrl:
      'https://support.drchrono.com/home/20708475397787-creating-a-new-request-ehi-export',
    sections: [
      {
        title: 'EHI Export (Self-Serve)',
        steps: [
          {
            text: 'Go to Reports > EHI Data Export in the left column.',
            mediaPlaceholder: '[Screenshot: DrChrono Reports > EHI Data Export menu]',
          },
          { text: 'Click New Export Request.' },
          { text: 'Select Individual (single patient) or Bulk (entire practice).' },
          { text: 'For bulk: export processes in batches of 50 patients.' },
          { text: 'Download link is sent to your Message Center. Download within 30 days.' },
        ],
        warning: 'Export files are permanently deleted after 30 days.',
      },
    ],
  },
  // {
  //   name: 'Practice Fusion',
  //   overview:
  //     'EHI Export produces tab-separated value (.tsv) files covering all patient data including demographics, appointments, insurance, and more.',
  //   tags: ['TSV', 'Comprehensive'],
  //   officialGuideUrl:
  //     'https://www.practicefusion.com/ehi-export-documentation/v4/index/',
  //   sections: [
  //     {
  //       title: 'EHI Export',
  //       steps: [
  //         { text: 'Log in to Practice Fusion.' },
  //         {
  //           text: 'Navigate to the EHI Export section.',
  //           mediaPlaceholder: '[Screenshot: Practice Fusion EHI Export navigation]',
  //         },
  //         { text: 'Request an export — the system generates TSV files organized by data type.' },
  //       ],
  //     },
  //     {
  //       title: 'Key Files in Your Export',
  //       steps: [
  //         { text: 'patient-demographics.tsv — core demographics' },
  //         { text: 'patient-appointments.tsv — appointment history' },
  //         { text: 'patient-insurance-eligibilities.tsv — insurance data' },
  //         { text: 'patient-encounters.tsv — encounter records' },
  //       ],
  //     },
  //   ],
  // },
  // {
  //   name: 'Valant',
  //   overview:
  //     'Export your patient list via the Reports module. Complete clinical data export requires contacting Valant support directly.',
  //   tags: ['Excel', 'Contact Support'],
  //   officialGuideUrl: 'https://help.valant.io/283906/700531-common-reporting-scenarios',
  //   sections: [
  //     {
  //       title: 'Export Patient List',
  //       steps: [
  //         { text: 'Go to the Reports module.' },
  //         {
  //           text: 'Run a Patient Report and click the export icon (top right) to export to Excel.',
  //           mediaPlaceholder: '[Screenshot: Valant Reports module with export icon]',
  //         },
  //         { text: 'Alternatively, use CSV Facesheet for a demographic export.' },
  //       ],
  //       warning:
  //         'For complete clinical data (notes, documents, billing), contact Valant support directly. There is no self-serve bulk export.',
  //     },
  //   ],
  // },
  // {
  //   name: 'Epic / MyChart',
  //   overview:
  //     'Epic is a hospital/large system EHR. Patients can self-export records via MyChart, or share via Share Everywhere. Provider-to-provider transfer uses Care Everywhere.',
  //   tags: ['Patient-initiated', 'PDF', 'FHIR'],
  //   officialGuideUrl: 'https://www.mychart.org/Sharing-Your-Medical-Record',
  //   sections: [
  //     {
  //       title: 'Patient Self-Export via MyChart',
  //       steps: [
  //         { text: 'Patient logs into their MyChart account.' },
  //         {
  //           text: 'Navigate to Health > Share My Record.',
  //           mediaPlaceholder: '[Screenshot: MyChart Share My Record page]',
  //         },
  //         { text: 'Choose "Request a copy" to download records as PDF (available for ~30 days).' },
  //         { text: 'Or choose "Share Everywhere" to generate a one-time code for the provider.' },
  //       ],
  //     },
  //   ],
  // },
  // {
  //   name: 'Headway',
  //   overview:
  //     'Headway is primarily a billing platform with basic EHR features. Download progress notes individually before deactivating your account.',
  //   tags: ['Manual download', 'Contact Support'],
  //   sections: [
  //     {
  //       title: 'Export Notes',
  //       steps: [
  //         { text: 'Before deactivating: download all progress notes individually from each client\'s session history.' },
  //         { text: 'Export your client list if needed (no documented bulk export).' },
  //         { text: 'To deactivate: contact Headway support directly.' },
  //       ],
  //       warning:
  //         'Headway does not have a self-serve bulk export. Download all notes before requesting account deactivation.',
  //     },
  //   ],
  // },
  {
    name: 'AdvancedMD',
    overview:
      'Export demographics and insurance as CSV, or use the bulk PM export for demographics and appointments.',
    tags: ['CSV', 'Microsoft Access'],
    officialGuideUrl: 'https://www.advancedmd.com/medical-office-software/data-export/',
    sections: [
      {
        title: 'Demographics & Insurance (CSV)',
        steps: [
          { text: 'Go to Reports > Patient Listings > Patient Transaction Report.' },
          { text: 'Export as CSV — includes demographics, insurance, and billing info.' },
        ],
      },
      {
        title: 'Bulk PM Data Export (Demographics & Appointments)',
        steps: [
          { text: 'Go to Utilities > Data Export.' },
          { text: 'Select data to include: demographics (always included) and appointments.' },
          { text: 'Export produces a Microsoft Access database file (.mdb).' },
        ],
        warning:
          'Requires Microsoft Access or SQL Server Management Studio to open the exported file.',
      },
    ],
  },
];
